import type { ArtifactProto, ExecutionStepProto } from "build-ts/chat_session";
import { ArtifactType, FileType, artifactTypeFromJSON } from "build-ts/common";
import type { ChatMessage } from "../../interfaces";
import type { ScheduledJobProto } from "build-ts/schedule";
import { getArtifactHeaderName, getArtifactType } from "./artifactUtils";

// ─── Text transforms ──────────────────────────────────────────────────────────

/** Transform [[dataframe: table_name]] → markdown link */
export const transformDataframeLinks = (text: string): string =>
  /**
   * IDs like spain-ads-cpl-outliers-(spend->-100) and those with &, +, > can
   * break/truncate markdown link parsing when inserted raw so doing encodeURIComponent
   */
  text.replace(/\[\[dataframe:\s*([^\]]+)\]\]/gi, (_, tableName) => {
    const cleanName = tableName.trim();
    return `[${cleanName}](#dataframe:${encodeURIComponent(cleanName)})`;
  });

/** Transform [[workflow: workflow_id]] → markdown link */
export const transformAnalysisLinks = (text: string): string =>
  /**
   * IDs like spain-ads-cpl-outliers-(spend->-100) and those with &, +, > can
   * break/truncate markdown link parsing when inserted raw so doing encodeURIComponent
   */
  text.replace(/\[\[workflow:\s*([^\]]+)\]\]/gi, (_, llmId) => {
    const cleanId = llmId.trim();
    return `[${cleanId}](#workflow:${encodeURIComponent(cleanId)})`;
  });

/** Transform [[chart: chart_name]] → markdown link */
export const transformChartLinks = (text: string): string =>
  text.replace(/\[\[chart:\s*([^\]]+)\]\]/gi, (_, chartName) => {
    const cleanName = chartName.trim();
    return `[${cleanName}](#chart:${encodeURIComponent(cleanName)})`;
  });

/** Transform [[code: snippet_name]] → markdown link that opens the artifact on the code tab */
export const transformCodeLinks = (text: string): string =>
  text.replace(/\[\[code:\s*([^\]]+)\]\]/gi, (_, snippetName) => {
    const cleanName = snippetName.trim();
    return `[${cleanName}](#code:${encodeURIComponent(cleanName)})`;
  });

/** Transform [[report:name]] / [[docx:name]] → markdown link that opens the report artifact */
export const transformReportLinks = (text: string): string =>
  text.replace(/\[\[(?:report|docx):\s*([^\]]+)\]\]/gi, (_, title) => {
    const cleanTitle = title.trim();
    return `[${cleanTitle}](#report:${encodeURIComponent(cleanTitle)})`;
  });

/**
 * Escapes currency-style dollar signs ($36,756) so remark-math doesn't treat
 * them as LaTeX delimiters. Math expressions always start with \, a letter,
 * or a space after $, never directly with a digit.
 */
export const escapeCurrencyDollar = (text: string): string => text.replace(/\$(?=\d)/g, "\\$");

/**
 * Normalizes LaTeX delimiters in markdown text.
 * Converts \[ and \] to $$ and \( and \) to $.
 */
export function normalizeLatex(markdown: string): string {
  return markdown.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$").replace(/\\\(/g, "$").replace(/\\\)/g, "$");
}

// ─── Artifact lookup helpers ──────────────────────────────────────────────────

/** Plain JSON from SSE uses string enum names and snake_case; persisted payloads may be normalized by fromJSON. */
export function getKgDataframePayload(artifact: ArtifactProto | Record<string, unknown>) {
  const a = artifact as Record<string, unknown>;
  return (a.kg_dataframe ?? a.kgDataframe) as { header?: { id?: string; name?: string } } | undefined;
}

export type ArtifactLookupOpts = {
  executionSteps?: ExecutionStepProto[];
  messageArtifacts?: ArtifactProto[];
  chatMessages?: ChatMessage[];
};

/**
 * Core traversal shared by all fallback find functions.
 * Walks executionSteps (newest-first) → messageArtifacts → chatMessages (newest-first,
 * including fileArtifacts on user turns).
 * The input name is assumed to already be trimmed (done in the transform step).
 * The .trim() on stored values guards against raw SSE data with unclean names.
 */
function findArtifactInMessageChain(
  matches: (a: ArtifactProto) => boolean,
  opts: ArtifactLookupOpts
): ArtifactProto | undefined {
  const steps = opts.executionSteps;
  if (steps?.length) {
    for (let i = steps.length - 1; i >= 0; i--) {
      for (const a of steps[i].artifacts) {
        if (matches(a as ArtifactProto)) return a as ArtifactProto;
      }
    }
  }

  const fromList = (list: ArtifactProto[] | undefined) => list?.find(x => matches(x));

  const found = fromList(opts.messageArtifacts);
  if (found) return found;

  const msgs = opts.chatMessages;
  for (let i = (msgs?.length ?? 0) - 1; i >= 0; i--) {
    const msg = msgs![i];
    const fromMsg = fromList(msg.artifacts);
    if (fromMsg) return fromMsg;
    const fromFileArtifacts = fromList(msg.fileArtifacts);
    if (fromFileArtifacts) return fromFileArtifacts;
    const st = msg.executionSteps;
    if (st?.length) {
      for (let j = st.length - 1; j >= 0; j--) {
        for (const a of st[j].artifacts) {
          if (matches(a as ArtifactProto)) return a as ArtifactProto;
        }
      }
    }
  }
  return undefined;
}

function nestedFileArtifactMatches(nested: ArtifactProto, name: string, fileType?: FileType): boolean {
  if (getArtifactType(nested) !== ArtifactType.FILE_ARTIFACT) return false;
  const fa = nested.file_artifact;
  if (!fa || getArtifactHeaderName(nested).trim() !== name.trim()) return false;
  if (fileType === undefined) return true;
  return fa.file_type === fileType;
}

function workflowRunNestedFileMatch(artifact: ArtifactProto, name: string, fileType?: FileType): boolean {
  const type = getArtifactType(artifact);
  if (type === ArtifactType.WORKFLOW_RUN) {
    const nested = [
      ...(artifact.workflow_run?.input_artifacts ?? []),
      ...(artifact.workflow_run?.output_artifacts ?? []),
    ];
    return nested.some(n => nestedFileArtifactMatches(n, name, fileType));
  }
  if (type === ArtifactType.WORKFLOW_RUN_SEARCH) {
    for (const run of artifact.workflow_run_search?.workflow_runs ?? []) {
      const nested = [...(run.input_artifacts ?? []), ...(run.output_artifacts ?? [])];
      if (nested.some(n => nestedFileArtifactMatches(n, name, fileType))) {
        return true;
      }
    }
  }
  return false;
}

/** When a search row matches, return a synthetic WORKFLOW_RUN parent for that run. */
function workflowRunParentFromSearchMatch(
  artifact: ArtifactProto,
  name: string,
  fileType?: FileType
): ArtifactProto | undefined {
  if (getArtifactType(artifact) !== ArtifactType.WORKFLOW_RUN_SEARCH) return undefined;
  for (const run of artifact.workflow_run_search?.workflow_runs ?? []) {
    const nested = [...(run.input_artifacts ?? []), ...(run.output_artifacts ?? [])];
    if (nested.some(n => nestedFileArtifactMatches(n, name, fileType))) {
      return { artifact_type: ArtifactType.WORKFLOW_RUN, workflow_run: run };
    }
  }
  return undefined;
}

function resolveWorkflowRunSearchParent(
  found: ArtifactProto | undefined,
  name: string,
  fileType?: FileType
): ArtifactProto | undefined {
  if (!found || getArtifactType(found) !== ArtifactType.WORKFLOW_RUN_SEARCH) return found;
  return workflowRunParentFromSearchMatch(found, name, fileType) ?? found;
}

/** Resolve [[dataframe:name]] — FILE_ARTIFACT, KG_DATAFRAME, DATAFRAME_ANALYSIS pandas_df, or run CSV. */
export function findKGDataframeArtifactByTableName(
  tableName: string,
  opts: ArtifactLookupOpts
): ArtifactProto | undefined {
  const found = findArtifactInMessageChain(artifact => {
    const type = artifactTypeFromJSON(artifact.artifact_type);
    if (type === ArtifactType.FILE_ARTIFACT) {
      return artifact.file_artifact?.header?.name?.trim() === tableName;
    }
    if (type === ArtifactType.KG_DATAFRAME) {
      return getKgDataframePayload(artifact)?.header?.name?.trim() === tableName;
    }
    if (type === ArtifactType.DATAFRAME_ANALYSIS) {
      return (artifact.dataframe_analysis?.pandas_dataframes ?? []).some(f => f.name?.trim() === tableName);
    }
    return workflowRunNestedFileMatch(artifact, tableName, FileType.CSV);
  }, opts);
  return resolveWorkflowRunSearchParent(found, tableName, FileType.CSV);
}

/** Resolve [[chart:name]] — FILE_ARTIFACT chart, DFA chart, or run chart sub-resource. */
export function findChartArtifactByName(chartName: string, opts: ArtifactLookupOpts): ArtifactProto | undefined {
  const trimmed = chartName.trim();
  const found = findArtifactInMessageChain(artifact => {
    const type = artifactTypeFromJSON(artifact.artifact_type);
    if (type === ArtifactType.FILE_ARTIFACT && artifact.file_artifact?.file_type === FileType.CHART_SPEC_JSON) {
      return artifact.file_artifact?.header?.name?.trim() === trimmed;
    }
    if (type === ArtifactType.DATAFRAME_ANALYSIS) {
      return (artifact.dataframe_analysis?.charts ?? []).some(c => c.name?.trim() === trimmed);
    }
    return workflowRunNestedFileMatch(artifact, trimmed, FileType.CHART_SPEC_JSON);
  }, opts);
  return resolveWorkflowRunSearchParent(found, trimmed, FileType.CHART_SPEC_JSON);
}

/** Resolve [[code:name]] to its DATAFRAME_ANALYSIS artifact (code tab). */
export function findCodeArtifactByName(artifactName: string, opts: ArtifactLookupOpts): ArtifactProto | undefined {
  return findArtifactInMessageChain(
    artifact =>
      artifactTypeFromJSON(artifact.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS &&
      artifact.dataframe_analysis?.header?.name?.trim() === artifactName,
    opts
  );
}

/**
 * Resolve [[report:name]] / [[docx:name]] — FILE_ARTIFACT doc, DFA report, or run doc sub-resource.
 */
export function findReportArtifactByName(reportName: string, opts: ArtifactLookupOpts): ArtifactProto | undefined {
  const trimmed = reportName.trim();
  const found = findArtifactInMessageChain(artifact => {
    const type = artifactTypeFromJSON(artifact.artifact_type);
    if (type === ArtifactType.FILE_ARTIFACT && artifact.file_artifact?.file_type === FileType.DOCX) {
      return artifact.file_artifact?.header?.name?.trim() === trimmed;
    }
    if (type === ArtifactType.DATAFRAME_ANALYSIS) {
      return (artifact.dataframe_analysis?.reports ?? []).some(r => r.name?.trim() === trimmed);
    }
    return workflowRunNestedFileMatch(artifact, trimmed, FileType.DOCX);
  }, opts);
  return resolveWorkflowRunSearchParent(found, trimmed, FileType.DOCX);
}

export type WorkflowLookupOpts = ArtifactLookupOpts & {
  scenarioJobs?: ScheduledJobProto[];
};

/** Resolve [[workflow:darwinName]] to a SCENARIO_JOB artifact. */
export function findWorkflowArtifactByDarwinName(
  darwinName: string,
  opts: WorkflowLookupOpts
): ArtifactProto | undefined {
  const isMatch = (a: ArtifactProto) =>
    artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
    (a.scenario_job?.scheduled_analysis as { darwin_name?: string } | undefined)?.darwin_name === darwinName;

  const found = findArtifactInMessageChain(isMatch, opts);
  if (found) return found;

  // scenarioJobs are tracked separately (not in message artifacts) — check them last.
  const job = opts.scenarioJobs?.find(
    j => (j.scheduled_analysis as { darwin_name?: string } | undefined)?.darwin_name === darwinName
  );
  if (job) return { artifact_type: ArtifactType.SCENARIO_JOB, scenario_job: job };
  return undefined;
}

export function getChartIndexInArtifact(artifact: ArtifactProto, chartName: string): number {
  if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return -1;
  return (artifact.dataframe_analysis?.charts ?? []).findIndex(c => c.name?.trim() === chartName);
}

export function getDataframeIndexInArtifact(artifact: ArtifactProto, dataframeName: string): number {
  if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return -1;
  return (artifact.dataframe_analysis?.pandas_dataframes ?? []).findIndex(df => df.name?.trim() === dataframeName);
}
